import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell,
  Search,
  User,
  LogOut,
  Settings,
  ChevronDown,
  Moon,
  Sun,
  Mail,
  CheckCircle,
  AlertCircle,
  ShieldCheck,
  X,
  ExternalLink,
  RefreshCw,
  Menu,
} from 'lucide-react';
import { GlassInput } from '../ui/GlassInput';
import { GlassButton } from '../ui/GlassButton';
import { GlassDrawer, GlassModal } from '../ui/GlassModal';
import { cn } from '../../utils/formatters';
import { useAuth } from '../../hooks/useAuth';
import { api } from '../../hooks/useApi';

const timeAgo = (value) => {
  if (!value) return '';
  const diff = Date.now() - new Date(value).getTime();
  const minutes = Math.max(Math.floor(diff / 60000), 0);
  if (minutes < 1) return 'agora';
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} h`;
  const days = Math.floor(hours / 24);
  return `${days} d`;
};

const notificationTone = (notification) => {
  const severity = notification.severity || notification.type;
  if (['critico', 'erro', 'danger'].includes(severity)) return 'danger';
  if (['alto', 'alta', 'medio', 'media', 'warning'].includes(severity)) return 'warning';
  if (['success', 'ok'].includes(severity)) return 'success';
  return 'info';
};

export const Header = ({ sidebarCollapsed, onMobileMenu }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [isNotificationsOpen, setIsNotificationsOpen] = useState(false);
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const [isProfileModalOpen, setIsProfileModalOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [notificationsLoading, setNotificationsLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [theme, setTheme] = useState(() => {
    if (typeof window === 'undefined') return 'dark';
    return localStorage.getItem('erp_theme') || 'dark';
  });

  const userInitials = (user?.name || user?.email || 'U')
    .split(' ')
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0])
    .join('')
    .toUpperCase();

  const handleLogout = () => {
    logout();
    navigate('/login', { replace: true });
  };

  useEffect(() => {
    document.documentElement.dataset.theme = theme;
    localStorage.setItem('erp_theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme((current) => (current === 'dark' ? 'light' : 'dark'));
  };

  const loadNotifications = useCallback(async () => {
    if (!user) return;
    setNotificationsLoading(true);
    try {
      const response = await api.get('/notifications', { params: { limit: 40 } });
      setNotifications(response.data.items || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      console.error('[notifications] erro ao carregar:', error);
    } finally {
      setNotificationsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadNotifications();
    const timer = setInterval(loadNotifications, 120000);
    return () => clearInterval(timer);
  }, [loadNotifications]);

  const openNotifications = async () => {
    setIsNotificationsOpen(true);
    await loadNotifications();
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await api.post(`/notifications/${notificationId}/read`);
      await loadNotifications();
    } catch (error) {
      console.error('[notifications] erro ao marcar como lida:', error);
    }
  };

  const dismissNotification = async (event, notificationId) => {
    event.stopPropagation();
    try {
      await api.post(`/notifications/${notificationId}/dismiss`);
      await loadNotifications();
    } catch (error) {
      console.error('[notifications] erro ao dispensar:', error);
    }
  };

  const markAllNotificationsRead = async () => {
    try {
      await api.post('/notifications/read-all');
      await loadNotifications();
    } catch (error) {
      console.error('[notifications] erro ao marcar todas:', error);
    }
  };

  const openNotificationAction = async (notification) => {
    await markNotificationRead(notification.id);
    if (notification.action_url) {
      setIsNotificationsOpen(false);
      navigate(notification.action_url);
    }
  };

  const openProfile = () => {
    setIsProfileOpen(false);
    setIsProfileModalOpen(true);
  };

  const openSettings = () => {
    setIsProfileOpen(false);
    navigate('/settings');
  };

  return (
    <>
      <motion.header
        initial={{ y: -20, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
        className={cn(
          'fixed top-0 right-0 z-[70] h-16 lg:h-20',
          'flex items-center justify-between px-4 lg:px-6',
          'glass-panel border-b border-white/10',
          'transition-all duration-300',
          sidebarCollapsed ? 'left-0 lg:left-20' : 'left-0 lg:left-72'
        )}
      >
        {/* Left side - Breadcrumb or Page Title */}
        <div className="flex items-center gap-4">
          <GlassButton variant="ghost" size="icon" onClick={onMobileMenu} className="lg:hidden" title="Abrir menu" aria-label="Abrir menu"><Menu className="h-5 w-5" /></GlassButton>
          <h1 className="text-xl font-semibold text-white/90">Dashboard</h1>
        </div>

        {/* Right side - Actions */}
        <div className="flex items-center gap-1.5 sm:gap-3">
          {/* Search */}
          <div className="relative hidden md:block">
            <GlassInput
              placeholder="Buscar..."
              icon={Search}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-64"
            />
          </div>

          {/* Notifications */}
          <div className="relative">
            <GlassButton
              variant="ghost"
              size="icon"
              onClick={openNotifications}
              className="relative"
              title="Notificações"
              aria-label="Notificações"
            >
              <Bell className="w-5 h-5" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-xs flex items-center justify-center font-medium animate-pulse">
                  {unreadCount}
                </span>
              )}
            </GlassButton>
          </div>

          {/* Theme toggle */}
          <GlassButton
            variant="ghost"
            size="icon"
            onClick={toggleTheme}
            title={theme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
            aria-label={theme === 'dark' ? 'Usar tema claro' : 'Usar tema escuro'}
          >
            {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
          </GlassButton>

          {/* Profile */}
          <div className="relative">
            <button
              onClick={() => setIsProfileOpen(!isProfileOpen)}
              className="flex items-center gap-3 px-3 py-2 rounded-xl hover:bg-white/5 transition-colors"
            >
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-accent-primary to-accent-secondary flex items-center justify-center">
                <span className="text-sm font-bold text-white">{userInitials}</span>
              </div>
              <div className="hidden md:block text-left">
                <p className="text-sm font-medium text-white/90">{user?.name || user?.email || 'Usuário'}</p>
                <p className="text-xs text-white/50">{user?.role || 'operador'}</p>
              </div>
              <ChevronDown className="hidden w-4 h-4 text-white/50 sm:block" />
            </button>

            {/* Profile dropdown */}
            <AnimatePresence>
              {isProfileOpen && (
                <>
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    className="fixed inset-0 z-[80]"
                    onClick={() => setIsProfileOpen(false)}
                  />
                  <motion.div
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.2 }}
                    className="absolute right-0 top-full mt-2 w-64 z-[90]"
                  >
                    <div className="glass-card p-2 space-y-1 shadow-2xl shadow-black/40">
                      <button
                        onClick={openProfile}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 transition-colors"
                      >
                        <User className="w-4 h-4" />
                        Perfil
                      </button>
                      <button
                        onClick={openSettings}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-white/80 hover:bg-white/10 transition-colors"
                      >
                        <Settings className="w-4 h-4" />
                        Configurações
                      </button>
                      <div className="h-px bg-white/10 my-1" />
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-red-400 hover:bg-red-500/10 transition-colors"
                      >
                        <LogOut className="w-4 h-4" />
                        Sair
                      </button>
                    </div>
                  </motion.div>
                </>
              )}
            </AnimatePresence>
          </div>
        </div>
      </motion.header>

      <GlassModal
        isOpen={isProfileModalOpen}
        onClose={() => setIsProfileModalOpen(false)}
        title="Perfil do usuário"
        description="Dados da sessão ativa no ERP"
        icon={ShieldCheck}
        size="md"
        footer={
          <>
            <GlassButton variant="ghost" onClick={() => setIsProfileModalOpen(false)}>
              Fechar
            </GlassButton>
            <GlassButton
              onClick={() => {
                setIsProfileModalOpen(false);
                navigate('/settings');
              }}
            >
              Administrar acessos
            </GlassButton>
          </>
        }
      >
        <div className="space-y-4">
          <div className="flex items-center gap-4 rounded-lg border border-white/10 bg-white/[0.03] p-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-lg bg-gradient-to-br from-accent-primary to-accent-secondary text-lg font-bold text-white">
              {userInitials}
            </div>
            <div>
              <p className="text-lg font-semibold text-white/90">{user?.name || 'Usuário'}</p>
              <p className="text-sm text-white/50">{user?.email || 'Sem e-mail cadastrado'}</p>
            </div>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <p className="text-xs uppercase tracking-wide text-white/35">Usuário</p>
              <p className="mt-1 text-sm text-white/80">{user?.username || '-'}</p>
            </div>
            <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
              <p className="text-xs uppercase tracking-wide text-white/35">Perfil</p>
              <p className="mt-1 text-sm text-white/80">{user?.role || '-'}</p>
            </div>
          </div>

          <div className="rounded-lg border border-white/10 bg-white/[0.03] p-3">
            <p className="text-xs uppercase tracking-wide text-white/35">Permissões ativas</p>
            <div className="mt-3 flex flex-wrap gap-2">
              {(user?.permissions || []).slice(0, 12).map((permission) => (
                <span key={permission} className="rounded-md border border-cyan-300/15 bg-cyan-400/10 px-2 py-1 text-xs text-cyan-100">
                  {permission}
                </span>
              ))}
              {(user?.permissions || []).length > 12 && (
                <span className="rounded-md border border-white/10 bg-white/5 px-2 py-1 text-xs text-white/50">
                  +{user.permissions.length - 12}
                </span>
              )}
            </div>
          </div>
        </div>
      </GlassModal>

      {/* Notifications Drawer */}
      <GlassDrawer
        isOpen={isNotificationsOpen}
        onClose={() => setIsNotificationsOpen(false)}
        position="right"
        size="md"
        title="Notificações"
      >
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <p className="text-sm font-medium text-white/80">{unreadCount} não lidas</p>
            <p className="text-xs text-white/40">Filtradas pelas permissões do seu usuário</p>
          </div>
          <div className="flex w-full items-center gap-2 sm:w-auto">
            <GlassButton variant="ghost" size="sm" onClick={loadNotifications} disabled={notificationsLoading}>
              <RefreshCw className={cn('w-4 h-4', notificationsLoading && 'animate-spin')} />
            </GlassButton>
            <GlassButton className="flex-1 sm:flex-none" variant="ghost" size="sm" onClick={markAllNotificationsRead} disabled={!unreadCount}>
              Marcar lidas
            </GlassButton>
          </div>
        </div>

        <div className="space-y-3">
          {!notificationsLoading && notifications.length === 0 && (
            <div className="rounded-xl border border-white/10 bg-white/[0.03] p-5 text-sm text-white/50">
              Nenhuma notificação para seu perfil agora.
            </div>
          )}
          {notifications.map((notification) => (
            <motion.div
              key={notification.id}
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              onClick={() => openNotificationAction(notification)}
              className={cn(
                'p-3 sm:p-4 rounded-xl border transition-colors cursor-pointer',
                notification.read
                  ? 'bg-white/[0.02] border-white/5'
                  : 'bg-accent-primary/5 border-accent-primary/20'
              )}
            >
              <div className="flex items-start gap-3">
                <div
                  className={cn(
                    'w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0',
                    notificationTone(notification) === 'success' && 'bg-green-500/20 text-green-400',
                    notificationTone(notification) === 'warning' && 'bg-yellow-500/20 text-yellow-400',
                    notificationTone(notification) === 'danger' && 'bg-red-500/20 text-red-400',
                    notificationTone(notification) === 'info' && 'bg-blue-500/20 text-blue-400'
                  )}
                >
                  {notificationTone(notification) === 'success' && <CheckCircle className="w-4 h-4" />}
                  {notificationTone(notification) === 'warning' && <AlertCircle className="w-4 h-4" />}
                  {notificationTone(notification) === 'danger' && <AlertCircle className="w-4 h-4" />}
                  {notificationTone(notification) === 'info' && <Mail className="w-4 h-4" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <p className={cn(
                      'text-sm font-medium leading-5',
                      notification.read ? 'text-white/70' : 'text-white'
                    )}>
                      {notification.title}
                    </p>
                    <span className="shrink-0 rounded-md border border-white/10 bg-white/5 px-2 py-0.5 text-[10px] uppercase text-white/40">
                      {notification.module}
                    </span>
                  </div>
                  <p className="text-sm leading-5 text-white/50 mt-0.5">{notification.message}</p>
                  <div className="mt-2 flex items-center justify-between gap-3 text-xs text-white/30">
                    <span>{timeAgo(notification.created_at)}</span>
                    {notification.action_label && (
                      <span className="inline-flex items-center gap-1 text-accent-primary">
                        {notification.action_label}
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex flex-col items-center gap-3">
                  {!notification.read && (
                    <div className="w-2 h-2 rounded-full bg-accent-primary flex-shrink-0" />
                  )}
                  <button
                    onClick={(event) => dismissNotification(event, notification.id)}
                    className="rounded-md p-1 text-white/30 hover:bg-white/10 hover:text-white/70"
                    title="Dispensar"
                    aria-label="Dispensar notificação"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </GlassDrawer>
    </>
  );
};

export default Header;
